import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  X, Play, Pause, Download, Phone, Clock, User,
  Loader2, Volume2, ChevronDown, ChevronUp, Mic, Share2
} from "lucide-react";
import axios from "axios";
import { useTimezone } from "../../contexts/TimezoneContext";
import { downloadOrShareFile } from "../../utils/pdfShare";
import CallRecordingPlayer from "../audio/CallRecordingPlayer";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  completed: { label: "Completed", color: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  "no-answer": { label: "No Answer", color: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  busy: { label: "Busy", color: "bg-orange-500/15 text-orange-500 border-orange-500/30" },
  failed: { label: "Failed", color: "bg-red-500/15 text-red-500 border-red-500/30" },
  connecting: { label: "Connecting", color: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
};

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDateTime(dateStr, tz) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const opts = tz ? { timeZone: tz } : {};
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", ...opts }) +
      " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...opts });
  } catch { return dateStr; }
}

// Build proxied URL that goes through our backend (Twilio requires auth).
// Prefers the permanent call_id (resolved server-side to the RecordingSid);
// keeps `url` as a fallback for legacy rows.
function getProxiedRecordingUrl(jobId, recording) {
  if (!recording?.recording_url && !recording?.call_id) return null;
  const token = localStorage.getItem("token");
  const params = new URLSearchParams();
  if (recording.call_id) params.set("call_id", recording.call_id);
  if (recording.recording_url) params.set("url", recording.recording_url);
  params.set("token", token || "");
  return `${API}/api/jobs/${jobId}/recordings/audio?${params.toString()}`;
}

// ─── Single Recording Card ────────────────────────────────────
function RecordingCard({ recording, isPlaying, onPlay, jobId, timezone }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[recording.status] || STATUS_CONFIG.completed;

  const proxiedUrl = getProxiedRecordingUrl(jobId, recording);
  const dateStr = (recording.created_at || recording.started_at || "").slice(0, 10) || "recording";
  const fileName = `Call_Recording_Job_${jobId}_${dateStr}.mp3`;

  const handleDownload = async () => {
    if (!proxiedUrl) return;
    const token = localStorage.getItem("token");
    try {
      // Native app: posts SHARE_FILE over the ReactNativeWebView bridge so the
      // iOS/Android system share sheet opens with the audio file attached.
      // Mobile web: uses Web Share API. Desktop: standard download.
      await downloadOrShareFile(proxiedUrl, fileName, "audio/mpeg", token);
    } catch (err) {
      if (err.name === "AbortError") return; // User cancelled share
      window.open(proxiedUrl, "_blank");
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden transition-all hover:border-[#FFCC00]/30"
      data-testid={`recording-card-${recording.call_id}`}
    >
      {/* Header */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`text-[10px] px-2 py-0 border ${status.color}`}>
                {status.label}
              </Badge>
              {recording.type === "pin_masked_call" && (
                <Badge className="text-[10px] px-2 py-0 border bg-purple-500/15 text-purple-400 border-purple-500/30">
                  <Mic className="w-2.5 h-2.5 mr-1" /> Masked Call
                </Badge>
              )}
              {recording.record_enabled !== false && recording.recording_url && (
                <Badge className="text-[10px] px-2 py-0 border bg-blue-500/15 text-blue-400 border-blue-500/30">
                  <Volume2 className="w-2.5 h-2.5 mr-1" /> Recorded
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {formatDateTime(recording.created_at || recording.started_at, timezone)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-mono font-bold text-foreground">
              {formatDuration(recording.duration || recording.recording_duration || 0)}
            </p>
          </div>
        </div>

        {/* Metadata row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {recording.technician_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" /> {recording.technician_name}
            </span>
          )}
          {recording.from_number && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" /> {recording.from_number}
            </span>
          )}
          {recording.pin_used && (
            <span className="font-mono text-[#FFCC00]">PIN: {recording.pin_used}</span>
          )}
        </div>

        {/* Audio Player (shared platform-wide component) */}
        {proxiedUrl ? (
          <CallRecordingPlayer
            src={proxiedUrl}
            knownDuration={recording.recording_duration || recording.duration || 0}
            fileName={fileName}
            active={isPlaying}
            onPlayStart={onPlay}
            showDownload={false}
            testId={`audio-player-${recording.call_id || recording.recording_sid || "rec"}`}
          />
        ) : (
          <p className="text-xs text-muted-foreground italic py-2">No audio file available for this call</p>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-1">
          {recording.recording_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="text-xs h-7 px-2.5 border-border"
              data-testid="download-recording-btn"
            >
              <span className="sm:hidden flex items-center"><Share2 className="w-3 h-3 mr-1" /> Share</span>
              <span className="hidden sm:flex items-center"><Download className="w-3 h-3 mr-1" /> Download</span>
            </Button>
          )}
          {/* Placeholder for future AI features */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            data-testid="expand-details-btn"
          >
            {expanded ? "Hide details" : "Details"}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-border bg-muted/30 p-4 space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            {recording.call_id && <Detail label="Call ID" value={recording.call_id} />}
            {recording.call_sid && <Detail label="SID" value={recording.call_sid.slice(0, 16) + "..."} />}
            {recording.to_number && <Detail label="To" value={recording.to_number} />}
            {recording.caller_id && <Detail label="Caller ID" value={recording.caller_id} />}
            {recording.recording_sid && <Detail label="Recording SID" value={recording.recording_sid} />}
          </div>
          {/* Future: AI Transcript & Summary sections will go here */}
          <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-dashed border-border text-center text-muted-foreground">
            AI Transcription & Summary — coming soon
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <span className="text-muted-foreground">{label}:</span>{" "}
    <span className="text-foreground font-mono">{value}</span>
  </div>
);

// ─── Main Modal ────────────────────────────────────────────
export default function CallRecordingsModal({ jobId, jobTitle, onClose }) {
  const { timezone } = useTimezone();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePlayerId, setActivePlayerId] = useState(null);

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/jobs/${jobId}/recordings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecordings(res.data.recordings || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load recordings");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[60] p-0 sm:p-6"
      onClick={onClose}
      data-testid="call-recordings-modal"
    >
      <div
        className="relative bg-background w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl border border-border flex flex-col overflow-hidden"
        style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.4)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/10 flex items-center justify-center flex-shrink-0">
              <Volume2 className="w-5 h-5 text-[#FFCC00]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate" data-testid="recordings-modal-title">
                Call Recordings
              </h2>
              <p className="text-xs text-muted-foreground truncate">
                {jobTitle || "Job"} — {recordings.length} recording{recordings.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
            data-testid="close-recordings-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#FFCC00]" />
              <p className="text-sm">Loading recordings...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-red-400 text-sm mb-3">{error}</p>
              <Button size="sm" variant="outline" onClick={fetchRecordings}>
                Try Again
              </Button>
            </div>
          ) : recordings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground" data-testid="no-recordings">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Volume2 className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <p className="text-sm font-medium mb-1">No call recordings available</p>
              <p className="text-xs max-w-xs">Call recordings will appear here once masked calls with recording enabled are completed for this job.</p>
            </div>
          ) : (
            recordings.map((rec) => (
              <RecordingCard
                key={rec.call_id || rec.recording_sid || Math.random()}
                recording={rec}
                jobId={jobId}
                timezone={timezone}
                isPlaying={activePlayerId === (rec.call_id || rec.recording_sid)}
                onPlay={() => setActivePlayerId(rec.call_id || rec.recording_sid)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
